const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}

export interface DownscaleResult {
  dataUrl: string;
  width: number;
  height: number;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("FileReader returned non-string"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export async function downscaleForImageSearchUpload(src: string): Promise<DownscaleResult> {
  try {
    if (typeof document === "undefined" || typeof Image === "undefined") {
      return { dataUrl: src, width: 0, height: 0 };
    }

    const img = await loadImage(src);
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (srcW <= 0 || srcH <= 0) return { dataUrl: src, width: 0, height: 0 };

    const maxEdge = Math.max(srcW, srcH);
    const scale = maxEdge > MAX_EDGE_PX ? MAX_EDGE_PX / maxEdge : 1;
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    if (w === srcW && h === srcH && src.startsWith("data:image/jpeg")) {
      return { dataUrl: src, width: srcW, height: srcH };
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { dataUrl: src, width: srcW, height: srcH };
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    return dataUrl?.startsWith("data:image/jpeg")
      ? { dataUrl, width: w, height: h }
      : { dataUrl: src, width: srcW, height: srcH };
  } catch {
    return { dataUrl: src, width: 0, height: 0 };
  }
}

export async function prepareImageSearchUploadDataUrl(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const out = await downscaleForImageSearchUpload(objectUrl);
    if (out.dataUrl.startsWith("data:image/")) return out.dataUrl;
    return await readFileAsDataUrl(file);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// ── Crop-flow downscale (port of apps/web/lib/downscale-image.ts) ─────────────
//
// Before the crop overlay grabs a region, downscale the whole image to a long
// edge ≤ 1600px. The SAME downscaled bitmap is used for ① display, ② region
// detect upload, and ③ crop confirm — so the DetectObject boxes (relative to the
// downscaled image), the overlay/crop coordinate conversions, and the final cut
// all share one coordinate system (no "detect on downscaled, display original"
// box misalignment).
//
// Contract:
//   - long edge ≤ MAX_EDGE_PX and src is data:image/* → return src as-is.
//   - long edge ≤ MAX_EDGE_PX but src is blob:/objectURL → re-encode to a stable
//     data-URL (blob:/objectURL can't go into sessionStorage and dies on a route
//     change, which would open a broken image on "reselect").
//   - long edge > MAX_EDGE_PX and decodable → downscale to long edge = MAX_EDGE_PX.
//   - any decode/encode failure → return src unchanged (degrade, never throw).

function isStableImageDataUrl(src: string): boolean {
  return src.startsWith("data:image/");
}

/**
 * Downscale a displayable src (blob:/data:) to a long edge ≤ 1600px JPEG
 * data-URL, returning the downscaled bitmap's pixel size too. Already-small /
 * any-failure → `dataUrl` returns the input unchanged (never throws);
 * width/height best-effort decode size. The size lets the CropModal convert
 * RegionBox/crop coordinates to percentages without depending on the DOM
 * `<img>` onLoad / naturalWidth (cached images never fire onLoad).
 */
export async function downscaleForImageSearch(src: string): Promise<DownscaleResult> {
  try {
    if (typeof document === "undefined" || typeof Image === "undefined") {
      return { dataUrl: src, width: 0, height: 0 };
    }

    const img = await loadImage(src);
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (srcW <= 0 || srcH <= 0) return { dataUrl: src, width: 0, height: 0 };

    const maxEdge = Math.max(srcW, srcH);
    const scale = maxEdge > MAX_EDGE_PX ? MAX_EDGE_PX / maxEdge : 1;
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));

    // Already within threshold and a persistable data-URL: keep as-is (no
    // re-encode, no extra JPEG loss). blob:/objectURL still gets re-encoded.
    if (w === srcW && h === srcH && isStableImageDataUrl(src)) {
      return { dataUrl: src, width: srcW, height: srcH };
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { dataUrl: src, width: srcW, height: srcH };
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);

    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    return dataUrl?.startsWith("data:image/")
      ? { dataUrl, width: w, height: h }
      : { dataUrl: src, width: srcW, height: srcH };
  } catch {
    return { dataUrl: src, width: 0, height: 0 };
  }
}

/**
 * Turn a picked File (or a data-URL) into a stable, downscaled data-URL ready
 * for display + crop. Port of apps/web/lib/prepare-image-search-src.ts.
 */
export async function prepareImageSearchSrc(input: File | string): Promise<string> {
  if (typeof input === "string") {
    const out = await downscaleForImageSearch(input);
    return out.dataUrl;
  }
  const objUrl = URL.createObjectURL(input);
  try {
    const out = await downscaleForImageSearch(objUrl);
    if (out.dataUrl.startsWith("data:image/")) return out.dataUrl;
    return await readFileAsDataUrl(input);
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/** Strip the "data:image/...;base64," prefix from a data-URL → pure base64. */
export function dataUrlToBase64(dataUrl: string): string {
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}
